import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Copy,
  Check,
  ShieldCheck,
  PhoneCall,
  CheckCircle2,
  Bot,
  Phone,
  Globe,
  Wifi,
  Zap,
  Lightbulb,
  Tv,
  GraduationCap,
  Wallet,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { getAppSetting, updateAppSetting } from '../../../api/services';

function WhatsAppBrandIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm0 18.15c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.32a8.19 8.19 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.24-8.22 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.17.25-.64.81-.79.97-.14.16-.29.18-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.24-1.47-1.39-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43s-.56-1.34-.76-1.84c-.2-.48-.41-.42-.56-.43h-.48c-.16 0-.43.06-.66.31-.22.25-.86.84-.86 2.05 0 1.21.88 2.38 1 2.54.12.16 1.73 2.64 4.18 3.7.58.25 1.04.4 1.39.51.59.19 1.12.16 1.55.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.16-.47-.28z" />
    </svg>
  );
}

const AppleSwitch = styled(Switch)(({ size }) => {
  const isSmall = size === 'small';
  const width = isSmall ? 38 : 46;
  const height = isSmall ? 22 : 26;
  const thumbSize = isSmall ? 18 : 22;
  const translate = isSmall ? 16 : 20;

  return {
    width,
    height,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: thumbSize + 3,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: `translateX(${translate - 3}px)`,
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      transitionDuration: '240ms',
      '&.Mui-checked': {
        transform: `translateX(${translate}px)`,
        color: '#FFFFFF',
        '& + .MuiSwitch-track': {
          backgroundColor: '#34C759',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.12)',
      transition: 'width 200ms ease, transform 200ms ease',
    },
    '& .MuiSwitch-track': {
      borderRadius: height / 2,
      backgroundColor: '#E5E7EB',
      opacity: 1,
      transition: 'background-color 240ms ease',
      boxSizing: 'border-box',
    },
  };
});

export default function WhatsappSettingSection({
  onSave,
  subscription,
  activeApp,
}) {
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const { activeApp: reduxActiveApp } = useSelector((state) => state.auth);
  let effectiveApp = activeApp || reduxActiveApp;
  if (!effectiveApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        effectiveApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          effectiveApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore
    }
  }

  const appName = effectiveApp?.name || 'VTU';
  const appSlug = effectiveApp?.slug || 'app';
  const appWebsite = effectiveApp?.urls?.websiteUrl || `https://${appSlug}.alisbuild.com`;
  const appPhone = effectiveApp?.owner?.phone || effectiveApp?.supportPhone || '';

  // Form states populated dynamically from active app
  const [formData, setFormData] = useState({
    enabled: true,
    businessName: `${appName} Assistant`,
    welcomeMessage: `👋 Welcome to ${appName} VTU Bot!\n\nI can help you purchase data, airtime, pay utility bills, and check your wallet balance 24/7.\n\nSelect an option below to get started:`,
    supportPhone: appPhone,
    supportUrl: `${appWebsite}/support`,
    dialFee: 0,
    completionFee: 0,
    enabledServices: {
      data: true,
      airtime: true,
      electricity: true,
      cable: true,
      exam: true,
      balanceCheck: true,
      fundWallet: true,
      history: true,
    },
  });

  useEffect(() => {
    let isMounted = true;
    const fetchSavedSettings = async () => {
      try {
        const res = await getAppSetting();
        if (res && res.ok && res.data?.data) {
          const savedBot = res.data.data.bot_settings?.whatsapp;
          if (savedBot && isMounted) {
            setFormData((prev) => ({
              ...prev,
              ...savedBot,
              enabledServices: {
                ...prev.enabledServices,
                ...(savedBot.enabledServices || {}),
              },
            }));
          }
        }
      } catch (e) {
        // ignore
      }
    };
    fetchSavedSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  // Read-only parameters provisioned by Super Admin
  const superAdminProvisioned = {
    phoneNumber: subscription?.serviceDetails?.phoneNumber || 'Pending Super Admin Provisioning',
    qualityRating: subscription?.serviceDetails?.qualityRating || (subscription?.status === 'active' ? 'GREEN' : 'Pending Verification'),
    messagingTier: subscription?.serviceDetails?.messagingTier || 'Standard (Tier 1)',
  };

  const servicesList = [
    {
      key: 'data',
      label: 'Data Bundles',
      desc: 'MTN, Airtel, Glo, 9mobile data',
      icon: <Wifi size={17} />,
      color: '#0071E3',
      bg: '#EFF6FF',
    },
    {
      key: 'airtime',
      label: 'Airtime VTU',
      desc: 'Instant airtime recharge',
      icon: <Zap size={17} />,
      color: '#F59E0B',
      bg: '#FEF3C7',
    },
    {
      key: 'electricity',
      label: 'Electricity Bills',
      desc: 'Prepaid & postpaid tokens',
      icon: <Lightbulb size={17} />,
      color: '#EAB308',
      bg: '#FEFCE8',
    },
    {
      key: 'cable',
      label: 'Cable TV',
      desc: 'DStv, GOtv, Startimes',
      icon: <Tv size={17} />,
      color: '#8B5CF6',
      bg: '#F5F3FF',
    },
    {
      key: 'exam',
      label: 'Exam PINs',
      desc: 'WAEC, NECO, JAMB tokens',
      icon: <GraduationCap size={17} />,
      color: '#EC4899',
      bg: '#FDF2F8',
    },
    {
      key: 'balanceCheck',
      label: 'Check Wallet Balance',
      desc: 'Instant balance inquiry',
      icon: <Wallet size={17} />,
      color: '#10B981',
      bg: '#ECFDF5',
    },
    {
      key: 'fundWallet',
      label: 'Fund Wallet',
      desc: 'Dedicated bank transfer info',
      icon: <CreditCard size={17} />,
      color: '#06B6D4',
      bg: '#ECFEFF',
    },
    {
      key: 'history',
      label: 'Transaction Receipts',
      desc: 'Recent purchase receipts',
      icon: <Receipt size={17} />,
      color: '#6366F1',
      bg: '#EEF2FF',
    },
  ];

  const enabledCount = Object.values(formData.enabledServices).filter(Boolean).length;

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.info(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleServiceToggle = (serviceKey) => {
    setFormData((prev) => ({
      ...prev,
      enabledServices: {
        ...prev.enabledServices,
        [serviceKey]: !prev.enabledServices[serviceKey],
      },
    }));
  };

  const handleEnableAllServices = () => {
    const updated = {};
    servicesList.forEach((s) => {
      updated[s.key] = true;
    });
    setFormData((prev) => ({ ...prev, enabledServices: updated }));
    toast.info('All WhatsApp services enabled');
  };

  const handleDisableAllServices = () => {
    const updated = {};
    servicesList.forEach((s) => {
      updated[s.key] = false;
    });
    setFormData((prev) => ({ ...prev, enabledServices: updated }));
    toast.info('All WhatsApp services disabled');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onSave) {
        await onSave(formData);
      } else {
        await updateAppSetting({
          bot_settings: {
            whatsapp: formData,
          },
        });
      }
      toast.success('WhatsApp Bot settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save WhatsApp Bot settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3.5} sx={{ width: '100%', pb: 6 }}>
      {/* 1. Apple Header Bar without Deactivate button */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2.5}
        >
          {/* Bot Branding */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: '#25D366',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 6px 18px rgba(37, 211, 102, 0.28)',
                flexShrink: 0,
              }}
            >
              <WhatsAppBrandIcon />
            </Box>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: '#111827',
                    letterSpacing: '-0.025em',
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  WhatsApp Cloud Bot
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    py: 0.35,
                    borderRadius: '999px',
                    bgcolor: '#ECFDF5',
                    color: '#059669',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                  Active
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25, fontSize: '0.88rem' }}>
                Meta Business Cloud API • Monthly Maintenance: ₦30,000/mo
              </Typography>
            </Box>
          </Stack>

          {/* Action Button */}
          <Box sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              startIcon={saving && <CircularProgress size={16} color="inherit" />}
              sx={{
                bgcolor: '#0071E3',
                color: '#FFFFFF',
                borderRadius: '999px',
                px: 3.5,
                py: 1.1,
                fontSize: '0.92rem',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  bgcolor: '#0077ED',
                  boxShadow: 'none',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* 2. Apple Card: Connected WhatsApp Channel Status */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.25, sm: 2.75 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '14px',
                bgcolor: '#ECFDF5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ShieldCheck size={24} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                CONNECTED WHATSAPP NUMBER
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 0.25 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.15rem' }}>
                  {superAdminProvisioned.phoneNumber}
                </Typography>
                <Tooltip title="Copy Phone Number">
                  <Button
                    size="small"
                    onClick={() => handleCopy(superAdminProvisioned.phoneNumber, 'Phone Number')}
                    sx={{
                      minWidth: 0,
                      p: 0.6,
                      borderRadius: '8px',
                      color: copiedField === 'Phone Number' ? '#059669' : '#6B7280',
                      bgcolor: copiedField === 'Phone Number' ? '#ECFDF5' : '#F9FAFB',
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {copiedField === 'Phone Number' ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </Tooltip>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.82rem' }}>
                Quality: <span style={{ color: '#059669', fontWeight: 700 }}>● {superAdminProvisioned.qualityRating}</span> • Limit: {superAdminProvisioned.messagingTier}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.85,
              px: 1.75,
              py: 0.75,
              borderRadius: '999px',
              bgcolor: '#ECFDF5',
              border: '1px solid rgba(16, 185, 129, 0.25)',
              color: '#059669',
              fontSize: '0.78rem',
              fontWeight: 700,
            }}
          >
            <CheckCircle2 size={16} />
            Verified &amp; Connected
          </Box>
        </Stack>
      </Paper>

      {/* 3. Apple Card: Bot Operation & Customer Experience */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack spacing={3}>
          {/* Header Row: Title & Online Switch */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.015em' }}>
                Bot Operation &amp; Customer Experience
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.85rem' }}>
                Configure channel availability, session service fees, and automated customer responses.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1,
                px: 1.75,
                borderRadius: '999px',
                bgcolor: formData.enabled ? '#F0FDF4' : '#F9FAFB',
                border: '1px solid',
                borderColor: formData.enabled ? 'rgba(34, 197, 94, 0.25)' : 'rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: formData.enabled ? '#15803D' : '#6B7280',
                    fontSize: '0.84rem',
                    lineHeight: 1.2,
                  }}
                >
                  {formData.enabled ? 'Channel Online' : 'Channel Paused'}
                </Typography>
                <Typography variant="caption" sx={{ color: formData.enabled ? '#16A34A' : '#9CA3AF', display: 'block', fontSize: '0.7rem' }}>
                  {formData.enabled ? 'Accepting chats & orders' : 'Temporarily suspended'}
                </Typography>
              </Box>
              <AppleSwitch
                disableRipple
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            </Box>
          </Stack>

          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

          {/* Section 1: Dial & Transaction Completion Charges */}
          <Box>
            <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              SESSION &amp; TRANSACTION SERVICE CHARGES
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.82rem', mt: 0.25, mb: 2 }}>
              Set custom service fees charged to customer wallets when they dial/initiate a session and upon transaction completion.
            </Typography>

            <Grid container spacing={2}>
              {/* Dial / Session Fee */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    borderColor: 'rgba(0,0,0,0.08)',
                    bgcolor: '#F9FAFB',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        bgcolor: '#EFF6FF',
                        color: '#0071E3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PhoneCall size={16} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                        Dial / Session Initiation Charge
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                        Charged when a customer dials the code or starts a session
                      </Typography>
                    </Box>
                  </Stack>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.dialFee}
                    onChange={(e) => setFormData({ ...formData, dialFee: Math.max(0, Number(e.target.value)) })}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per session</Typography>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                      },
                    }}
                  />
                </Paper>
              </Grid>

              {/* Completion Fee */}
              <Grid item xs={12} sm={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '16px',
                    borderColor: 'rgba(0,0,0,0.08)',
                    bgcolor: '#F9FAFB',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        bgcolor: '#ECFDF5',
                        color: '#059669',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle2 size={16} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
                        Transaction Completion Charge
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                        Convenience surcharge added upon successful order completion
                      </Typography>
                    </Box>
                  </Stack>
                  <TextField
                    fullWidth
                    type="number"
                    value={formData.completionFee}
                    onChange={(e) => setFormData({ ...formData, completionFee: Math.max(0, Number(e.target.value)) })}
                    placeholder="0.00"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per order</Typography>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{
                      bgcolor: '#FFFFFF',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                      },
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Pricing Formula Preview Banner */}
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: '12px',
                bgcolor: '#F0F7FF',
                border: '1px solid rgba(0, 113, 227, 0.15)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: '#0071E3', fontWeight: 600 }}>
                💡 Customer Fee Formula:
              </Typography>
              <Typography variant="caption" sx={{ color: '#1E40AF' }}>
                Customer pays <strong>₦{formData.dialFee || 0}</strong> on session start + Product Price + <strong>₦{formData.completionFee || 0}</strong> on completion.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

          {/* Section 2: Brand & Support Details */}
          <Box>
            <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em' }}>
              BRAND &amp; SUPPORT DETAILS
            </Typography>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Bot Display Name"
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  helperText="Displayed in greeting headers and receipts"
                  placeholder="e.g. SpeedyData Assistant"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Bot size={18} color="#6B7280" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Customer Support Hotline"
                  value={formData.supportPhone}
                  onChange={(e) => setFormData({ ...formData, supportPhone: e.target.value })}
                  helperText="Provided when customers ask for human support"
                  placeholder="e.g. +234 801 234 5678"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone size={18} color="#6B7280" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Help Center / Website URL"
                  value={formData.supportUrl}
                  onChange={(e) => setFormData({ ...formData, supportUrl: e.target.value })}
                  helperText="Link sent to customers for online FAQs"
                  placeholder="e.g. https://speedydata.com.ng"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Globe size={18} color="#6B7280" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Welcome & Greeting Auto-Responder"
                  value={formData.welcomeMessage}
                  onChange={(e) => setFormData({ ...formData, welcomeMessage: e.target.value })}
                  helperText="Initial interactive message sent automatically when a customer dials in or messages the bot"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </Paper>

      {/* 4. Apple Card: Active Services Matrix */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: '24px',
          bgcolor: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
        }}
      >
        <Stack spacing={2.5}>
          {/* Header with Title, Count badge and Enable/Disable All */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.015em' }}>
                  Active Services on WhatsApp
                </Typography>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    borderRadius: '999px',
                    bgcolor: '#F3F4F6',
                    color: '#4B5563',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                  }}
                >
                  {enabledCount} of {servicesList.length} enabled
                </Box>
              </Stack>
              <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.85rem' }}>
                Toggle which VTU products and self-service menus are available to customers.
              </Typography>
            </Box>

            {/* Quick Batch Actions */}
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={handleEnableAllServices}
                sx={{
                  color: '#0071E3',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.4,
                  bgcolor: '#F0F7FF',
                  '&:hover': { bgcolor: '#E0F0FE' },
                }}
              >
                Enable All
              </Button>
              <Button
                size="small"
                onClick={handleDisableAllServices}
                sx={{
                  color: '#6B7280',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: 1.25,
                  py: 0.4,
                  bgcolor: '#F3F4F6',
                  '&:hover': { bgcolor: '#E5E7EB' },
                }}
              >
                Disable All
              </Button>
            </Stack>
          </Stack>

          {/* Apple Inset Grouped List of Services */}
          <Paper
            variant="outlined"
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              borderColor: 'rgba(0,0,0,0.08)',
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            }}
          >
            {servicesList.map((svc, index) => {
              const isChecked = !!formData.enabledServices[svc.key];
              const isLast = index === servicesList.length - 1;

              return (
                <React.Fragment key={svc.key}>
                  <Box
                    onClick={() => handleServiceToggle(svc.key)}
                    sx={{
                      py: 1.75,
                      px: { xs: 2, sm: 2.75 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      bgcolor: isChecked ? 'transparent' : 'rgba(0,0,0,0.015)',
                      '&:hover': {
                        bgcolor: isChecked ? 'rgba(0, 113, 227, 0.025)' : '#F9FAFB',
                      },
                    }}
                  >
                    {/* Left: Icon + Title & Description */}
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, pr: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '12px',
                          bgcolor: isChecked ? svc.bg : '#F3F4F6',
                          color: isChecked ? svc.color : '#9CA3AF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {svc.icon}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isChecked ? '#111827' : '#6B7280',
                            fontSize: '0.94rem',
                            lineHeight: 1.3,
                          }}
                        >
                          {svc.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: isChecked ? '#6B7280' : '#9CA3AF',
                            fontSize: '0.82rem',
                            mt: 0.25,
                          }}
                        >
                          {svc.desc}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Right: Apple Switch */}
                    <Box onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                      <AppleSwitch
                        disableRipple
                        checked={isChecked}
                        onChange={() => handleServiceToggle(svc.key)}
                      />
                    </Box>
                  </Box>
                  {!isLast && (
                    <Divider
                      sx={{
                        ml: { xs: 7.25, sm: 8 },
                        borderColor: 'rgba(0,0,0,0.06)',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
}

WhatsappSettingSection.propTypes = {
  onSave: PropTypes.func,
  subscription: PropTypes.object,
  activeApp: PropTypes.object,
};
